import { useCallback } from "react";
import { useSetAtom, useAtomValue } from "jotai";
import { togglesAtom } from "@zapier/canvas-state";
import { Link } from "@zapier/design-system";
import { Popover } from "@zapier/design-system-beta";
import {
  AllPlansType,
  AssetType,
  ChatbotsPlanName,
  emitEventOnChatbotsPlanUpgradeSetter,
  emitEventOnInterfacesPlanUpgradeSetter,
  emitEventOnTablesPlanUpgradeSetter,
  getUpgradePlanType,
} from "@zapier/canvas-state";
import useIsOwnerAdminOrSuperAdmin from "../../hooks/useIsOwnerAdminOrSuperAdmin";

type BaseProps = {
  assetType: Exclude<AssetType, undefined | "zap">;
  triggerRef: React.RefObject<HTMLElement>;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
};

type TableProps = BaseProps & {
  assetType: "table";
  currentPlanLimit: number;
};

type ChatbotProps = BaseProps & {
  assetType: "chatbot";
  currentPlanName: ChatbotsPlanName;
};

type InterfaceProps = BaseProps & {
  assetType: "interface";
  currentPlanName: AllPlansType;
};

type Props = TableProps | ChatbotProps | InterfaceProps;

const TITLE = "Plan Limit Reached";

export function UpsellPopover(props: Props) {
  const { assetType, triggerRef, isOpen, onOpenChange } = props;
  const isAdmin = useIsOwnerAdminOrSuperAdmin();
  const { isProjectNeopolitanEnabled } = useAtomValue(togglesAtom);

  const emitEventOnChatbotsPlanUpgrade = useSetAtom(
    emitEventOnChatbotsPlanUpgradeSetter,
  );
  const emitEventOnTablesPlanUpgrade = useSetAtom(
    emitEventOnTablesPlanUpgradeSetter,
  );
  const emitEventOnInterfacesPlanUpgrade = useSetAtom(
    emitEventOnInterfacesPlanUpgradeSetter,
  );

  const assetTypePlural = `${assetType}s`;

  const getHref = useCallback(() => {
    if (isProjectNeopolitanEnabled) {
      return `/app/planbuilder/plans`;
    }

    if (assetType === "chatbot") {
      const planType = getUpgradePlanType({
        type: "chatbots",
        currentPlanName: (props as ChatbotProps).currentPlanName,
      });
      return `/app/planbuilder/add-ons?product=chatbots-${planType}`;
    }

    if (assetType === "table") {
      const planType = getUpgradePlanType({
        type: "tables",
        currentPlanLimit: (props as TableProps).currentPlanLimit,
      });
      return `/app/planbuilder/add-ons?product=tables-${planType}`;
    }

    if (assetType === "interface") {
      const planType = getUpgradePlanType({
        type: "interfaces",
        currentPlanName: (props as InterfaceProps).currentPlanName,
      });
      return `/app/planbuilder/add-ons?product=interfaces-${planType}`;
    }
  }, [assetType, props, isProjectNeopolitanEnabled]);

  const emitEvents = useCallback(() => {
    const message = `Upgrade to create more ${assetTypePlural}.`;

    if (assetType === "chatbot") {
      emitEventOnChatbotsPlanUpgrade(message);
    } else if (assetType === "table") {
      emitEventOnTablesPlanUpgrade(message);
    } else if (assetType === "interface") {
      emitEventOnInterfacesPlanUpgrade(message);
    }
  }, [
    assetType,
    assetTypePlural,
    emitEventOnChatbotsPlanUpgrade,
    emitEventOnTablesPlanUpgrade,
    emitEventOnInterfacesPlanUpgrade,
  ]);

  return (
    <Popover
      triggerRef={triggerRef}
      placement="top left"
      offset={10}
      color="light"
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      aria-label={TITLE}
      title={TITLE}
    >
      <>
        {`You've reached your plan's ${assetType} limit. `}
        {isAdmin ? (
          <>
            <Link href={getHref()} target="_blank" onClick={emitEvents}>
              Upgrade
            </Link>
            {` to create more ${assetTypePlural}.`}
          </>
        ) : (
          `Talk to your admin about upgrading to create more ${assetTypePlural}.`
        )}
      </>
    </Popover>
  );
}
